import { useEffect, useRef, useState } from 'react';
import { requestExport, exportError, type ExportOptions } from '../../../services/tableExport';
import { type ApiError } from '../../../services/apiError';

/** Each download snapshots its criteria; newer criteria/navigation cancel it. */
export function useTableExport(endpoint: string, query: string) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [message, setMessage] = useState('');
  const active = useRef<AbortController | null>(null);
  const last = useRef<ExportOptions | null>(null);

  useEffect(() => {
    setBusy(false); setError(null); setMessage(''); last.current = null;
    return () => { active.current?.abort(); active.current = null; };
  }, [endpoint, query]);

  const run = async (options: ExportOptions) => {
    if (!endpoint || active.current || (error?.retryAt && error.retryAt > Date.now())) return;
    const controller = new AbortController(); active.current = controller;
    last.current = options;
    setBusy(true); setError(null); setMessage('');
    try {
      await requestExport(endpoint, query, options, controller.signal);
      if (!controller.signal.aborted) setMessage('Your export download has started.');
    } catch (failure) {
      const result = await exportError(failure);
      if (!controller.signal.aborted) setError(result);
    } finally {
      if (active.current === controller) { active.current = null; setBusy(false); }
    }
  };
  return { busy, error, message, run, retry: () => last.current && run(last.current) };
}
